<template>
  <ion-header>
    <ion-toolbar>
      <ion-title>Choose Icon</ion-title>
      <ion-buttons slot="end">
        <ion-button @click="doneButton">Done</ion-button>
      </ion-buttons>
    </ion-toolbar>
  </ion-header>
  <ion-content class="ion-padding">
    <!--<template v-for="item in icons" :key="item.id">-->
    <!--<RecycleScroller :items="icons" class="scroller" :item-size="60" key-field="id">
      <ChooseIconListElement class="scroll-item" @click='emit("update:value", item);' :logo="item"/>
    </RecycleScroller>
    <template>-->
    <ion-searchbar v-model="search" />
    <ion-radio-group v-model="chosenIcon" allow-empty-selection>
      <RecycleScroller
        class="scroller"
        :items="filteredIcons"
        :item-size="60"
        key-field="id"
        @update="onScrollerUpdate"
        v-slot="{ item }"
      >
        <ChooseIconListElement
          class="scroll-item"
          :logo="item"
          @select="chosenIcon = $event"
        />
      </RecycleScroller>
    </ion-radio-group>
  </ion-content>
</template>

<script setup lang="ts">
import {
  IonContent,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonRadioGroup,
  IonButtons,
  IonButton,
  IonSearchbar,
} from "@ionic/vue";
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from "vue";
import ChooseIconListElement from "@/components/ChooseIconListElement.vue";
import type { FontAwesomeIcon } from "@/models/api";
import { type IconString, iconStringToIconObject } from "@/helper/icons";
import { undefinedToNull } from "@/helper/undefinedToNull";
import { useApiStore } from "@/stores/api";
import { nextItemsToPrefetch } from "@/helper/iconPrefetch";

const api = useApiStore();

defineComponent({
  name: "ChooseIconModal",
});

const props = defineProps<{
  modelValue: FontAwesomeIcon | null;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: FontAwesomeIcon | null): void;
  (e: "save", value: FontAwesomeIcon | null): void;
}>();
onMounted(() => {
  // noinspection TypeScriptValidateJSTypes
  requestAnimationFrame(() => api.fetchAllIconNames());
});
const chosenIcon = ref<FontAwesomeIcon | IconString | null>(props.modelValue);

watch(chosenIcon, (newVal, oldVal) => {
  console.log("choosenIcon:", newVal);
  const icon = normalizeIcon(newVal);
  if (newVal !== oldVal) {
    emit("update:modelValue", icon);
  }
});

// const options = computed(() => id.value !== null ? api.byId(id.value) : null );
const icons = computed(() => api.allIconNames);

type RecycleScrollerIndexPart = {
  id: IconString;
};
type RecycleScrollerItem = RecycleScrollerIndexPart & FontAwesomeIcon;

const flattenedIcons = computed<RecycleScrollerItem[]>(() =>
  icons.value.map(iconStringToIconObject).map(
    (item: FontAwesomeIcon) =>
      Object.assign(
        // eslint-disable-next-line
        { id: `${item.type}.${item.name}.${item.style}`.toLowerCase() } as RecycleScrollerIndexPart,
        // eslint-disable-next-line
        item,
      ) as RecycleScrollerItem
  )
);
const search = ref("");
const filteredIcons = computed(() => {
  if (!flattenedIcons.value) {
    return flattenedIcons.value;
  }
  const query = search.value.trim().toLowerCase();
  return flattenedIcons.value.filter(
    (item) => item.id.indexOf(query) !== -1
  );
});

const PREFETCH_DEBOUNCE_MS = 100;
let prefetchTimer: ReturnType<typeof setTimeout> | null = null;
let visibleRange: [number, number] = [0, 0];

// Debounced, so a fast fling only prefetches where it comes to rest.
function onScrollerUpdate(start: number, end: number) {
  visibleRange = [start, end];
  schedulePrefetch();
}

function schedulePrefetch() {
  if (prefetchTimer !== null) {
    clearTimeout(prefetchTimer);
  }
  prefetchTimer = setTimeout(() => {
    prefetchTimer = null;
    const [start, end] = visibleRange;
    // The visible rows are fetched by their ApiIcon (deduplicated against this batch).
    api.fetchIcons(nextItemsToPrefetch(filteredIcons.value, start, end).map(
      ({ type, name, style }) => ({ type, name, style })
    ));
  }, PREFETCH_DEBOUNCE_MS);
}

// The scroller reports no range yet when the list (or the filter) changes: cover the first pages.
watch(filteredIcons, () => {
  visibleRange = [0, 0];
  schedulePrefetch();
});
onBeforeUnmount(() => {
  if (prefetchTimer !== null) {
    clearTimeout(prefetchTimer);
  }
});

function doneButton() {
  emit("save", normalizeIcon(chosenIcon.value));
}

function normalizeIcon(
  value: FontAwesomeIcon | IconString | null | undefined
): FontAwesomeIcon | null {
  const normalizedValue = undefinedToNull(value);
  return typeof normalizedValue === "string"
    ? iconStringToIconObject(normalizedValue)
    : normalizedValue;
}
</script>

<style scoped lang="scss">
p.description {
  color: dimgray;
}

.scroller {
  height: 100%;
  .item-inner {
    height: 60px;
  }
}
</style>
